import { api, getSession, type DocumentRegister, type MemberList, type NotificationSettings, type OrgOverview, type OrgPerson } from '../api';
import { callout, field, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { bindDocumentViewers, bindDownloads, docTitle, fmtBytes, removeDocument, reviseDocument, uploadDocument, verifyIntegrity } from '../dialogs/document';
import { HOURS_FORMAT_LABEL, HOURS_FORMATS, fmtHoursMinutes } from '../hours';
import { DATE_FORMAT_LABEL, DATE_FORMATS, formatDate, type DateFormat } from '../dates';
import { addSomebody, bindMembers, membersCard } from './members';
import { bindRecords, recordsCard, type DriveDestination } from './records';
import { errorCard, esc, fmtDate, html, loading, pill, tile, toast } from '../ui';

/**
 * Organisation info — who works here, what it is approved to do, and who it bills.
 *
 * Also where a new organisation is made to work. A freshly created one has no
 * customer, no approval and nobody authorised to sign, which means "Add
 * aircraft" fails and no aircraft can be released — so the outstanding items
 * are stated at the top, each with the reason it matters and a button that
 * fixes it, instead of being discovered one failed form at a time.
 */

const AUTH_SHORT: Record<string, string> = {
  ARC_ISSUE: 'ARC issue',
  ARC_RECOMMEND: 'ARC recommend',
  CRS_145: 'CRS · Part-145',
  CRS_ML801: 'CRS · ML.A.801(b)(2)',
  PILOT_OWNER: 'Pilot-owner',
  AMP_APPROVE: 'AMP approve',
};

const APPROVAL_LABEL: Record<string, string> = {
  CAO: 'Part-CAO',
  CAMO: 'Part-CAMO',
  PART_145: 'Part-145',
  PART_M_F: 'Part-M Subpart F',
};

/**
 * The languages a document can be issued in.
 *
 * Short on purpose, and it mirrors DOCUMENT_LANGUAGES on the server. Offering
 * more here than the server can produce would let somebody choose a language
 * their paperwork would never come out in. Each is named in itself, because
 * that is how somebody looking for their own language finds it in a list.
 */
const DOC_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ro', label: 'Română (Romanian)' },
];

const CUSTOMER_KIND: Record<string, string> = {
  COMPANY: 'Company',
  INDIVIDUAL: 'Individual',
  CLUB: 'Club',
  ATO: 'ATO',
};

const expiryState = (iso: string | null): 'OK' | 'DUE_SOON' | 'OVERDUE' | 'NA' => {
  if (!iso) return 'NA';
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  return days < 0 ? 'OVERDUE' : days <= 60 ? 'DUE_SOON' : 'OK';
};

/**
 * The organisation as it appears on paper.
 *
 * Shown as a card rather than hidden behind the edit button, because the
 * invoice numbering is the sort of setting whose current value matters — an
 * organisation moving from another system needs to see where the series stands
 * before it issues anything.
 */
function detailsCard(o: OrgOverview): string {
  const org = o.organisation;
  const address = [org.addressLine1, org.addressLine2, [org.postCode, org.city].filter(Boolean).join(' '), org.countryCode]
    .filter(Boolean)
    .join(', ');
  const year = new Date().getUTCFullYear();
  const nextReference = org.invoiceIncludeYear
    ? `${org.invoiceSeries}-${year}-${String(org.invoiceNextNumber).padStart(4, '0')}`
    : `${org.invoiceSeries}-${String(org.invoiceNextNumber).padStart(4, '0')}`;

  const line = (label: string, value: string | null, missing: string) => html`
    <div class="row">
      <div class="bd">
        <div class="t">${esc(label)}</div>
        <div class="m">${value ? esc(value) : html`<span class="dim">${esc(missing)}</span>`}</div>
      </div>
    </div>
  `;

  // Folded away by default: this is reference data that is set once and then
  // rarely looked at, and it was pushing the people and approvals — the part of
  // this page that is actually worked with — below the fold.
  return html`
    <details class="card">
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>Organisation details</h2>
            <p class="tiny dim">What appears on an invoice, and the numbering it is issued under</p>
          </div>
        </div>
        <button class="btn sm" data-open="edit-organisation">Edit</button>
      </summary>
      <div class="cols2">
        <div class="rows">
          ${line('Legal name', org.legalName ?? org.name, 'trading name used')}
          ${line('Address', address || null, 'no address on file — an invoice has to carry one')}
          ${line('Contact', [org.phone, org.email, org.website].filter(Boolean).join(' · ') || null, 'none recorded')}
          ${line('Registration', [org.companyRegistration, org.vatNumber ? `VAT ${org.vatNumber}` : null].filter(Boolean).join(' · ') || null, 'no company or VAT number')}
        </div>
        <div class="rows">
          ${line('Bank', [org.bankName, org.bankAccount, org.bankBic].filter(Boolean).join(' · ') || null, 'nowhere for a customer to pay')}
          ${line('VAT rate', org.vatRatePercent === null ? null : `${org.vatRatePercent}% on new invoices`, 'not set — invoices would be raised with no VAT')}
          ${line('Next invoice', nextReference, '')}
          ${line(
            'Stock markup',
            org.stockMarkupPercent === null
              ? null
              : `${org.stockMarkupPercent}% added to what a part cost, to price it`,
            'not set — parts received would have no sale price and would not be billed',
          )}
          ${line(
            'Stores label reference',
            org.labelReference,
            'not set — printed labels carry no form reference',
          )}
          ${line('Currency', `${org.defaultCurrency} · ${org.timeZone}`, '')}
        </div>
      </div>

      ${featuresRow(o)}

      <div class="card-f">
        The next number is what the next invoice takes, so it can be set to continue a series started
        elsewhere. It only ever moves forward — a number already issued cannot be handed out twice.
      </div>
    </details>
  `;
}

/**
 * What this installation has turned on for this organisation.
 *
 * Shown to everybody, editable only by the provider. It explains a whole
 * missing menu item, so hiding it from the organisation that is missing it
 * would be the least useful place to keep the answer — but a local admin who
 * switched off a store that already held stock would hide a register they are
 * required to keep, and have no way back in to find it.
 */
function featuresRow(o: OrgOverview): string {
  const on = o.organisation.storesEnabled;
  return html`
    <div class="rows" style="margin-top:10px">
      <div class="row">
        <div class="bd">
          <div class="t">
            <label style="display:flex;gap:8px;align-items:center;cursor:${o.canToggleStores ? 'pointer' : 'default'}">
              <input type="checkbox" data-feature-stores ${on ? 'checked' : ''}
                     ${o.canToggleStores ? '' : 'disabled'}>
              Stores
            </label>
          </div>
          <div class="m">
            ${on
              ? 'Receipt, incoming inspection, segregation, issue to a work pack and the stock check.'
              : 'Turned off — this organisation manages airworthiness without holding parts.'}
          </div>
          <div class="m tiny dim">
            ${o.canToggleStores
              ? 'You can change this because your account administers the installation.'
              : 'Set by your provider. A pure CAMO holds no stock; the parts live wherever the maintenance is done.'}
          </div>
        </div>
      </div>
    </div>
  `;
}

function setupCard(o: OrgOverview): string {
  if (o.ready) {
    return callout('ok', '✓', html`
      This organisation is set up: an owner is on file, an approval is recorded, and staff hold the
      authorisations needed to release an aircraft and carry out a review.
    `);
  }
  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>Before this organisation can manage an aircraft</h2>
          <p class="tiny dim">Each of these blocks something concrete — none of them is optional</p>
        </div>
        ${pill('DUE_SOON', `${o.blockers.length} outstanding`)}
      </div>
      <div class="rows">
        ${o.blockers
          .map(
            (b) => html`
              <div class="row" style="border-left-color:var(--caution)">
                <div class="bd">
                  <div class="t">${esc(b.what)}</div>
                  <div class="m">${esc(b.why)}</div>
                </div>
                <button class="btn sm" data-open="${esc(b.action)}">Fix</button>
              </div>
            `,
          )
          .join('')}
      </div>
    </div>
  `;
}

/**
 * What reaches this person's inbox.
 *
 * Their own preference only. A digest repeats what they can already open, so
 * setting somebody else's would be a way to post them a fleet they cannot see.
 */
function notificationsCard(n: NotificationSettings, canTest: boolean): string {
  const p = n.preference;
  const off = p.frequency === 'OFF';
  const box = (name: string, label: string, on: boolean) => html`
    <label class="tiny" style="display:flex;align-items:center;gap:6px;cursor:pointer">
      <input type="checkbox" data-notif="${esc(name)}" ${on ? 'checked' : ''}> ${esc(label)}
    </label>
  `;

  // Folded, like the organisation details above it. This is a preference set
  // once and left alone; the working part of this page is the staff list.
  return html`
    <details class="card">
      <summary class="card-h">
        <div class="sum">
          <span class="twist" aria-hidden="true"></span>
          <div>
            <h2>What you are told about</h2>
            <p class="tiny dim">
              ${off
                ? 'Nothing is sent — open to turn it on'
                : `${p.frequency === 'DAILY' ? 'Every morning' : 'Once a week'}, ${p.horizonDays} days ahead`}
            </p>
          </div>
        </div>
        <div style="display:flex;gap:6px;align-items:center">
          ${n.lastSentAt ? pill('OK', `last sent ${esc(fmtDate(n.lastSentAt))}`) : pill('NA', 'never sent')}
          <button class="btn sm" data-notif-preview>What would it say?</button>
        </div>
      </summary>
      <div class="card-b">
        <p class="tiny dim" style="margin:0 0 12px">
          A digest of what is already on your screens — nothing here is a new judgement, and an
          empty one is never sent.
        </p>
        <div class="frow3">
          ${field('How often', select('frequency', n.frequencies
            .map((f: string) => opt(f, f === 'OFF' ? 'Never' : f === 'DAILY' ? 'Every morning' : 'Once a week', f === p.frequency))
            .join(''), 'data-notif-frequency'))}
          ${field('On which day', select('weekday', [1, 2, 3, 4, 5, 6, 7]
            .map((d) => opt(String(d), ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][d - 1], d === p.weekday))
            .join(''), p.frequency === 'WEEKLY' ? '' : 'disabled'), 'Only for a weekly digest')}
          ${field('How far ahead', input('horizonDays', String(p.horizonDays),
            'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'), 'Days')}
        </div>

        <div style="display:flex;gap:14px;flex-wrap:wrap;margin-top:10px">
          ${box('includeOverdue', 'Past its limit', p.includeOverdue)}
          ${box('includeDueSoon', 'Coming due', p.includeDueSoon)}
          ${box('includeArcExpiry', 'ARCs expiring', p.includeArcExpiry)}
          ${box('includeDefects', 'Grounding defects', p.includeDefects)}
          ${box('includeDeferrals', 'Deferrals running out', p.includeDeferrals)}
          ${box('includeShelfLife', 'Stock going out of date', p.includeShelfLife)}
        </div>

        <div style="margin-top:12px;display:flex;gap:8px;align-items:center;flex-wrap:wrap">
          <button class="btn pri" data-notif-save>Save</button>
          ${
            // Administrators only, matching the endpoint. Offered even when the
            // preference is off: whether mail works and whether you want a
            // digest are separate questions, and the first is the one you need
            // answered before trusting a schedule with anything.
            canTest && n.mailConfigured
              ? html`<button class="btn" data-notif-test>Send a test message</button>`
              : ''
          }
          ${n.sendingTo
            ? html`<span class="tiny dim">to ${esc(n.sendingTo)}</span>`
            : html`<span class="tiny" style="color:var(--caution)">no address on your account — nothing can be sent</span>`}
        </div>
      </div>
      ${!n.mailConfigured
        ? callout('caution', '!', html`
            <b>This installation has no mail server configured</b>, so a digest is written to the log
            rather than sent. The preference is still saved.
          `)
        : ''}
      ${off
        ? '<div class="card-f">Nothing is sent while this is set to never.</div>'
        : ''}
    </details>
  `;
}

function view(
  o: OrgOverview,
  reg: DocumentRegister,
  notifications: NotificationSettings,
  members: MemberList | null,
  drive: DriveDestination | null,
): string {
  const org = o.organisation;
  const arsCount = o.people.filter((p) =>
    p.authorisations.some((a) => a.kind === 'ARC_ISSUE' || a.kind === 'ARC_RECOMMEND'),
  ).length;
  const crsCount = o.people.filter((p) => p.authorisations.some((a) => a.kind.startsWith('CRS'))).length;

  return html`
    <div class="page-h">
      <div>
        <h1>Organisation info</h1>
        <p class="dim">
          ${esc(org.approvalReference ?? org.name)} · nominated posts, authorisations and the documents that
          define scope
        </p>
      </div>
      <div style="display:flex;gap:8px">
        <button class="btn" data-open="add-customer">Add owner</button>
        <button class="btn" data-open="add-approval">Add approval</button>
        <button class="btn pri" data-open="add-person">Add somebody</button>
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Staff', value: o.people.length, sub: 'currently employed' })}
      ${tile({ label: 'May release', value: crsCount, sub: 'hold a CRS authorisation', tone: crsCount ? 'ok' : 'exceed' })}
      ${tile({ label: 'Review staff', value: arsCount, sub: 'may issue or recommend', tone: arsCount ? 'ok' : 'caution' })}
      ${tile({ label: 'Owners', value: o.customers.length, sub: `${o.aircraftCount} aircraft managed` })}
    </div>

    ${setupCard(o)}
    ${detailsCard(o)}
    ${members ? membersCard(members) : ''}
    ${members ? recordsCard(o, getSession()?.organisations ?? [], drive) : ''}
    ${notificationsCard(notifications, Boolean(members))}

    <div class="card">
      <div class="card-h">
        <div><h2>Staff &amp; authorisations</h2></div>
        <div style="display:flex;align-items:center;gap:10px">
          ${(o.formerPeople ?? []).length
            ? html`<label class="tiny" style="display:flex;align-items:center;gap:5px;cursor:pointer">
                <input type="checkbox" data-show-former>
                Former staff (${(o.formerPeople ?? []).length})
              </label>`
            : ''}
          <span class="lbl">scope-limited privileges</span>
        </div>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr><th>Name</th><th>Role</th><th>Authorisation scope</th><th class="col-opt">Licence</th><th>Licence expiry</th><th></th></tr>
          </thead>
          <tbody>
            ${
              o.people.length
                ? o.people.map((p) => personRow(p)).join('')
                : '<tr><td colspan="6" class="muted">Nobody on the roster yet.</td></tr>'
            }
          </tbody>
          <tbody data-former hidden>
            ${(o.formerPeople ?? []).map((p) => personRow(p)).join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Every signature stores the authorisation held at the moment of signing — later scope changes never
        rewrite past records.
        <br><br>
        This is the <b>staff record</b>, and it is not a login. It exists whether or not the person
        uses FlyPack CAMO, and it stays after they leave, because the certificates they signed name them
        for as long as those records are kept. Giving somebody a way in is
        <b>Who can log in</b> above — and one person can hold several roles there, which in a club
        this size is the normal case rather than the exception.
      </div>
    </div>

    <div class="cols3">
      <div class="card">
        <div class="card-h"><h2>Approval scope</h2></div>
        <div class="rows">
          ${
            o.approvals.length
              ? o.approvals
                  .map(
                    (a) => html`
                      <div class="row">
                        <div class="bd">
                          <div class="t">${esc(APPROVAL_LABEL[a.kind] ?? a.kind)} — ${esc(a.reference)}</div>
                          <div class="m">
                            ${esc(a.scopeNote ?? 'No scope note recorded')}
                            ${a.regimeCsv ? ` · ${esc(a.regimeCsv.replace(/,/g, ', '))}` : ''}
                            ${a.privilegesCsv ? ` · ${esc(a.privilegesCsv.replace(/,/g, ', '))}` : ''}
                          </div>
                        </div>
                        ${a.expiresAt ? pill(expiryState(a.expiresAt), fmtDate(a.expiresAt)) : ''}
                      </div>
                    `,
                  )
                  .join('')
              : '<div class="row"><div class="bd"><div class="m">No approval recorded yet.</div></div></div>'
          }
        </div>
      </div>

      <div class="card">
        <div class="card-h">
          <div>
            <h2>Exposition &amp; procedures</h2>
            <p class="tiny dim">
              ${reg.storage.files} file(s) · ${esc(fmtBytes(reg.storage.bytes))}${reg.storage.flagged ? ` · ${reg.storage.flagged} flagged` : ''}
            </p>
          </div>
          <div style="display:flex;gap:6px">
            <button class="btn sm" data-doc="verify">Verify</button>
            <button class="btn pri sm" data-doc="upload">Add</button>
          </div>
        </div>
        <div class="rows">
          ${
            orgDocs(reg).length
              ? orgDocs(reg)
                  .map(
                    (d) => html`
                      <div class="row">
                        <div class="bd">
                          <div class="t">${docTitle(d, d.title + (d.revision ? ` ${d.revision}` : ''))}</div>
                          <div class="m">
                            ${esc(d.kind)}${d.revisionDate ? ` · ${esc(fmtDate(d.revisionDate))}` : ''}${d.attachment ? ` · ${esc(fmtBytes(d.attachment.byteSize))}` : ' · no file'}
                          </div>
                        </div>
                        ${d.attachment
                          ? `<button class="btn sm" data-download="${esc(d.attachment.id)}" data-name="${esc(d.attachment.fileName)}">Download</button>`
                          : ''}
                        ${d.status === 'CURRENT' ? `<button class="btn sm" data-revise="${esc(d.id)}">Revise</button>` : ''}
                        ${d.status === 'CURRENT' ? `<button class="btn sm" data-remove="${esc(d.id)}">Remove</button>` : ''}
                        ${pill(
                          d.attachment?.integrityOk === false ? 'OVERDUE' : d.status === 'CURRENT' ? 'OK' : 'DUE_SOON',
                          d.attachment?.integrityOk === false ? 'File problem' : d.status === 'CURRENT' ? 'Current' : 'Superseded',
                        )}
                      </div>
                    `,
                  )
                  .join('')
              : '<div class="row"><div class="bd"><div class="m">No exposition documents yet.</div></div></div>'
          }
        </div>
        <div class="card-f">Stored in ${esc(reg.storage.folder)}</div>
      </div>

      <div class="card">
        <div class="card-h"><h2>Owners &amp; contracts</h2></div>
        <div class="rows">
          ${
            o.customers.length
              ? o.customers
                  .map(
                    (c) => html`
                      <div class="row">
                        <div class="bd">
                          <div class="t">${esc(c.name)}${c.aircraftCount ? ` — ${c.aircraftCount} aircraft` : ''}</div>
                          <div class="m">
                            ${esc(CUSTOMER_KIND[c.kind] ?? c.kind)}${c.registrations.length ? ` · ${esc(c.registrations.join(', '))}` : ' · no aircraft yet'}
                            ${c.billingEmail ? ` · ${esc(c.billingEmail)}` : ''}
                          </div>
                        </div>
                        <button class="btn sm" data-edit-customer="${esc(c.id)}">Edit</button>
                      </div>
                    `,
                  )
                  .join('')
              : '<div class="row"><div class="bd"><div class="m">No owners on file. An aircraft cannot be added without one.</div></div></div>'
          }
          ${o.contracts
            .map(
              (c) => html`
                <div class="row">
                  <div class="bd">
                    <div class="t">${esc(c.reference)}</div>
                    <div class="m">
                      ${esc(c.customerName ?? 'unknown owner')} · ${esc(c.basis.replace(/_/g, '.'))}
                      ${c.validTo ? ` · valid to ${esc(fmtDate(c.validTo))}` : ''}
                    </div>
                  </div>
                  ${c.validTo ? pill(expiryState(c.validTo), 'Valid') : ''}
                </div>
              `,
            )
            .join('')}
        </div>
      </div>
    </div>

    ${ratesCard(o)}
  `;
}

/**
 * The organisation's prices.
 *
 * Always rendered, including when there is no card at all — an organisation
 * without one prices every visit at zero and bills none of it, and showing
 * nothing was how that came to have no explanation anywhere on screen.
 */
function ratesCard(o: OrgOverview): string {
  const items = o.rateCard?.items ?? [];
  const currency = o.rateCard?.currency ?? o.organisation.defaultCurrency;
  const has = (code: string) => items.some((i) => i.code === code && (i.unitPrice ?? 0) > 0);

  // Only the codes the application bills from on its own are worth warning
  // about. A missing RECORDS_IMPORT costs nothing; a missing engineering hour
  // means signed-off labour is never recorded as chargeable.
  const missing = (['ENGINEERING_HOUR', 'ARC_ISSUE'] as const).filter((c) => !has(c));
  const drivenBy = new Map(o.rateCardCodes.map((c) => [c.code, c.drives]));

  return html`
    <div class="card">
      <div class="card-h">
        <div>
          <h2>${esc(o.rateCard?.name ?? 'Rates')}</h2>
          <p class="tiny dim">
            What an hour, a month and a review are worth — work packs and reviews bill from this card
          </p>
        </div>
        <button class="btn sm" data-open="edit-rates">${o.rateCard ? 'Edit' : 'Set rates'}</button>
      </div>

      ${missing.length
        ? callout('caution', '!', html`
            ${o.rateCard
              ? html`This card has no price for
                  ${missing.map((c) => html`<b>${esc(c)}</b>`).join(' and ')}.`
              : html`No rate card on file.`}
            Work signed off is recorded as done but never as chargeable, so it will not appear on an
            invoice and nothing will say it is missing. Airworthiness is unaffected — a release is never
            held up by a price list.
          `)
        : ''}

      ${items.length
        ? html`
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>Code</th><th>Description</th><th>Unit</th>
                    <th class="n">Price</th><th class="col-opt">Billed</th>
                  </tr>
                </thead>
                <tbody>
                  ${items
                    .map(
                      (i) => html`<tr>
                        <td class="mono">${esc(i.code)}</td>
                        <td>
                          ${esc(i.description)}
                          ${i.appliesToRegime ? html`<span class="sub2">${esc(i.appliesToRegime)}</span>` : ''}
                        </td>
                        <td class="tiny muted">${esc(i.unit.toLowerCase())}</td>
                        <td class="n">${(i.unitPrice ?? 0).toFixed(2)} ${esc(currency)}</td>
                        <td class="col-opt tiny muted">${esc(drivenBy.get(i.code) ?? 'Invoiced by hand')}</td>
                      </tr>`,
                    )
                    .join('')}
                </tbody>
              </table>
            </div>
          `
        : html`<div class="card-b muted">
            Nothing priced yet. Until there is, a work pack shows no cost and a signed release raises no
            charge.
          </div>`}
    </div>
  `;
}

function personRow(p: OrgPerson): string {
  const state = expiryState(p.licenceExpiry);
  const left = Boolean(p.employedTo);
  return html`
    <tr${left ? ' class="dim-row"' : ''}>
      <td>
        <b>${esc(p.displayName)}</b>${p.userId ? ' <span class="chip">login</span>' : ''}
        ${left ? html`<div class="tiny muted">left ${esc(fmtDate(p.employedTo!))}</div>` : ''}
      </td>
      <td>${esc(p.jobTitle ?? '—')}</td>
      <td class="tiny muted">
        ${
          p.authorisations.length
            ? p.authorisations.map((a) => esc(AUTH_SHORT[a.kind] ?? a.kind)).join(' · ') +
              (left ? ' — held at leaving' : '')
            : 'none — cannot sign'
        }
      </td>
      <td class="n col-opt">${esc(p.licence ?? '—')}</td>
      <td>${p.licenceExpiry ? pill(state, fmtDate(p.licenceExpiry)) : '<span class="muted">—</span>'}</td>
      <td style="white-space:nowrap">
        ${left
          // Nothing to do to a leaver's record: it is kept as it stood, which is
          // what makes the signatures pointing at it mean anything.
          ? html`<span class="tiny muted">record closed</span>`
          : html`
            <button class="btn sm" data-edit-person="${esc(p.id)}">Edit</button>
            <button class="btn sm" data-grant="${esc(p.id)}">Authorise</button>
            <button class="btn sm" data-leaving="${esc(p.id)}">Left</button>
          `}
      </td>
    </tr>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

/**
 * Record that someone has left.
 *
 * Deliberately not called "remove": nothing is deleted. Their record stays,
 * because the releases and certificates they signed name them, and it stops
 * being offered anywhere work is assigned or signed off.
 */
function recordLeaving(p: OrgPerson, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: `${p.displayName} has left`,
    sub: 'The record is kept — it is what the signatures on past releases point at',
    primary: 'Record leaving date',
    body: () => html`
      ${callout('info', 'i', html`
        Nothing is deleted. ${esc(p.displayName)} stops appearing wherever work is assigned or signed
        off — releases, reviews, defects, the tech log and training — from this date.
        ${p.authorisations.length
          ? html`Their ${p.authorisations.length} authorisation${p.authorisations.length === 1 ? '' : 's'}
              stay on file as the record of what was held when they signed.`
          : ''}
      `)}
      <div class="frow">
        ${field('Last day', input('employedTo', todayISO(), 'type="date"'))}
        ${field('Why', input('reason', '', 'placeholder="Resigned · retired · contract ended" maxlength="400"'))}
      </div>
    `,
    collect: (root) => {
      const reason = val(root, 'reason');
      if (!reason.trim()) return { error: 'Say why they left — the next audit asks who was in post and when.' };
      return { employedTo: val(root, 'employedTo'), reason };
    },
    submit: (body) => api.organisation.recordLeaving(p.id, body),
    done: (r) => `${r.displayName} recorded as leaving on ${r.employedTo}.`,
    onDone: (r: { lostCapability: string | null }) => {
      // The warning is the point: an organisation that has just lost its only
      // certifying staff should hear it here, not from a pack it cannot release
      // a week later.
      if (r.lostCapability) toast(r.lostCapability, 'error');
      reload();
    },
  });
}

/**
 * Adding an owner, and correcting one.
 *
 * One dialog for both. The fields are identical and the only difference is
 * whether they arrive filled in — two dialogs would be two places for the
 * list of owner kinds to drift apart.
 *
 * No reason is asked for. An owner's details are commercial rather than
 * airworthiness facts: unlike the aircraft counters or a personnel licence,
 * nothing already signed is read against them. It is still audited.
 */
function addCustomer(reload: () => void, existing?: OrgOverview['customers'][number]): void {
  openDialog<Record<string, unknown>>({
    title: existing ? 'Edit owner' : 'Add owner',
    sub: existing
      ? `${existing.name}${existing.aircraftCount ? ` — ${existing.aircraftCount} aircraft` : ''}`
      : 'Every aircraft belongs to an owner or customer — the party the ML.A.201(f) contract is with',
    primary: existing ? 'Save' : 'Add owner',
    body: () => html`
      <div class="frow">
        ${field('Name', input('name', existing?.name ?? '', 'placeholder="Aeroclub Băneasa" maxlength="200"'))}
        ${field('Kind', select('kind', [
          opt('CLUB', 'Flying club', existing?.kind === 'CLUB'),
          opt('COMPANY', 'Company', existing?.kind === 'COMPANY'),
          opt('INDIVIDUAL', 'Individual owner', existing?.kind === 'INDIVIDUAL'),
          opt('ATO', 'ATO', existing?.kind === 'ATO'),
        ].join('')))}
      </div>
      <div class="frow">
        ${field('Billing email', input('billingEmail', existing?.billingEmail ?? '', 'type="email" maxlength="254"'))}
        ${field('VAT number', input('vatNumber', existing?.vatNumber ?? '', 'maxlength="32"'))}
      </div>
      ${field('Payment terms (days)', input('paymentTermsDays', existing?.paymentTermsDays ?? 30, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'),
        'What an invoice\'s due date is counted from')}
      ${existing?.registrations.length
        ? callout('info', 'i', html`
            Renaming an owner renames them on <b>every invoice already issued</b> — the invoice
            carries the customer, not a copy of the name. Correct a typo here; a genuine change of
            owner is a new owner record and the aircraft moved to it.
          `)
        : ''}
    `,
    collect: (root) => {
      const name = val(root, 'name');
      if (!name) return { error: 'The owner needs a name.' };
      return {
        name,
        kind: val(root, 'kind'),
        billingEmail: val(root, 'billingEmail') || undefined,
        vatNumber: val(root, 'vatNumber') || undefined,
        paymentTermsDays: numVal(root, 'paymentTermsDays'),
      };
    },
    submit: (payload) =>
      existing
        ? api.organisation.updateCustomer(existing.id, payload)
        : api.organisation.createCustomer(payload),
    done: (r: { name: string; changed?: string[]; unchanged?: boolean }) =>
      !existing
        ? `${r.name} added — an aircraft can now be registered to them`
        : r.unchanged
          ? 'Nothing changed'
          : `${r.name} updated — ${(r.changed ?? []).map((f) => OWNER_FIELD[f] ?? f).join(', ')}`,
    onDone: reload,
  });
}

/** What a changed owner field is called when the toast names it. */
const OWNER_FIELD: Record<string, string> = {
  name: 'name',
  kind: 'kind',
  vatNumber: 'VAT number',
  billingEmail: 'billing email',
  paymentTermsDays: 'payment terms',
};

function addApproval(reload: () => void): void {
  let kind = 'CAO';
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Add approval',
    sub: 'What this organisation is approved to do, and under which reference',
    primary: 'Record approval',
    body: () => html`
      <div class="frow">
        ${field('Approval', select('kind', [
          opt('CAO', 'Part-CAO — management and maintenance', kind === 'CAO'),
          opt('CAMO', 'Part-CAMO — continuing airworthiness management', kind === 'CAMO'),
          opt('PART_145', 'Part-145 — maintenance organisation', kind === 'PART_145'),
          opt('PART_M_F', 'Part-M Subpart F — maintenance organisation', kind === 'PART_M_F'),
        ].join(''), 'data-kind'))}
        ${field('Reference', input('reference', '', 'placeholder="CAO.RO.0042" style="font-family:var(--mono)" maxlength="40"'))}
      </div>
      ${field('Scope', input('scopeNote', '', 'placeholder="Part-ML aeroplanes below 2730 kg, piston" maxlength="400"'))}
      <div class="frow">
        ${field('Regimes it may manage', select('regimeCsv', [
          opt('', 'Not specified'),
          opt('PART_ML', 'Part-ML'),
          opt('PART_ML,PART_M_G', 'Part-ML and Part-M Subpart G'),
          opt('PART_M_G', 'Part-M Subpart G'),
        ].join('')))}
        ${field('Privileges held', select('privilegesCsv', [
          opt('', 'None beyond the base approval'),
          opt('ARC_ISSUE', 'Issue airworthiness review certificates'),
          opt('ARC_ISSUE,ARC_EXTEND', 'Issue and extend ARCs'),
        ].join('')))}
      </div>
      <div class="frow">
        ${field('Issued', input('issuedAt', '', 'type="date"'))}
        ${field('Expires', input('expiresAt', '', 'type="date"'), 'Leave blank for an open-ended approval')}
      </div>
      ${
        kind === 'CAO'
          ? callout('info', 'i', html`
              A Part-CAO may hold continuing airworthiness management <b>and</b> maintenance privileges, so it can
              both manage an aircraft and release it. It may not manage an aircraft listed on the AOC of a
              licensed air carrier — that needs a Part-CAMO.
            `)
          : kind === 'PART_145' || kind === 'PART_M_F'
            ? callout('info', 'i', html`
                A maintenance approval performs work and releases aircraft. It does not manage continuing
                airworthiness, so it will not be offered when choosing what an aircraft is managed under.
              `)
            : ''
      }
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
        kind = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const reference = val(root, 'reference');
      if (!reference) return { error: 'The approval reference is required.' };
      return {
        kind: val(root, 'kind'),
        reference,
        scopeNote: val(root, 'scopeNote') || undefined,
        regimeCsv: val(root, 'regimeCsv') || undefined,
        privilegesCsv: val(root, 'privilegesCsv') || undefined,
        issuedAt: val(root, 'issuedAt') || undefined,
        expiresAt: val(root, 'expiresAt') || undefined,
      };
    },
    submit: (payload) => api.organisation.createApproval(payload),
    done: (r) => `${r.reference} recorded`,
    onDone: reload,
  });
}

function editOrganisation(o: OrgOverview, reload: () => void): void {
  const org = o.organisation;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Organisation details',
    sub: `${org.legalName ?? org.name} · ${org.approvalReference ?? 'no approval reference'}`,
    primary: 'Save',
    body: () => html`
      <div class="fset">
        <div class="lg">Who is issuing</div>
        <div class="frow">
          ${field('Trading name', input('name', org.name, 'maxlength="200"'))}
          ${field('Legal name', input('legalName', org.legalName ?? '', 'maxlength="200"'), 'As registered, if it differs')}
        </div>
        ${field('Address', input('addressLine1', org.addressLine1 ?? '', 'maxlength="200"'))}
        ${field('Address, continued', input('addressLine2', org.addressLine2 ?? '', 'maxlength="200"'))}
        <div class="frow3">
          ${field('Post code', input('postCode', org.postCode ?? '', 'maxlength="20"'))}
          ${field('City', input('city', org.city ?? '', 'maxlength="120"'))}
          ${field('Country', input('countryCode', org.countryCode ?? '', 'maxlength="2" style="text-transform:uppercase"'))}
        </div>
        <div class="frow3">
          ${field('Phone', input('phone', org.phone ?? '', 'maxlength="40"'))}
          ${field('Email', input('email', org.email ?? '', 'type="email" maxlength="254"'))}
          ${field('Website', input('website', org.website ?? '', 'maxlength="200"'))}
        </div>
        <div class="frow">
          ${field('Company registration', input('companyRegistration', org.companyRegistration ?? '', 'maxlength="40" placeholder="J40/1234/2019"'))}
          ${field('VAT number', input('vatNumber', org.vatNumber ?? '', 'maxlength="32" placeholder="RO12345678"'))}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Where the money goes</div>
        <div class="frow3">
          ${field('Bank', input('bankName', org.bankName ?? '', 'maxlength="120"'))}
          ${field('Account / IBAN', input('bankAccount', org.bankAccount ?? '', 'maxlength="60" style="font-family:var(--mono)"'))}
          ${field('BIC', input('bankBic', org.bankBic ?? '', 'maxlength="20" style="font-family:var(--mono)"'))}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Invoicing</div>
        <div class="frow3">
          ${field(
            'VAT rate',
            input('vatRatePercent', org.vatRatePercent ?? '', 'type="number" min="0" max="100" step="0.01"'),
            'Percent applied to new invoices. 0 if not VAT registered',
          )}
          ${field(
            'Stock markup',
            input('stockMarkupPercent', org.stockMarkupPercent ?? '', 'type="number" min="0" max="500" step="0.1"'),
            'Added to a part’s cost to set its sale price, when it is received',
          )}
          ${field(
            'Stores label reference',
            input('labelReference', org.labelReference ?? '', 'placeholder="PAG-F C.3.2-2" maxlength="40" style="font-family:var(--mono)"'),
            'Printed small in the corner of every printed label — the page of your exposition it belongs to',
          )}
          ${field('Series', input('invoiceSeries', org.invoiceSeries, 'maxlength="12" style="font-family:var(--mono);text-transform:uppercase"'))}
          ${field(
            'Next number',
            input('invoiceNextNumber', org.invoiceNextNumber, 'type="number" min="1" step="1"'),
            'What the next invoice takes',
          )}
          ${field(
            'Invoice in',
            select('defaultCurrency', ['RON', 'EUR', 'USD', 'GBP', 'CHF']
              .map((c) => opt(c, c, c === org.defaultCurrency))
              .join('')),
            'What new invoices are raised in — the rate card is what work is priced in',
          )}
        </div>
        <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
          <input type="checkbox" data-year ${org.invoiceIncludeYear ? 'checked' : ''} style="margin-top:3px">
          <span>Put the year in the reference — <b data-preview style="font-family:var(--mono)"></b></span>
        </label>
        ${callout('info', 'i', html`
          Changing any of this affects invoices drafted from now on. Ones already issued keep the details and
          the number they were issued under, so an invoice never changes after the fact.
        `)}
      </div>

      <div class="fset">
        <div class="lg">What the paperwork says</div>
        <div class="frow3">
          ${field(
            'Documents are issued in',
            select('documentLanguage', DOC_LANGUAGES
              .map((l) => opt(l.code, l.label, l.code === (org.documentLanguage ?? 'en')))
              .join('')),
            'The language that governs, where the two texts ever disagree',
          )}
          ${field(
            'Hours are written',
            select('hoursFormat', HOURS_FORMATS
              .map((f) => opt(f, HOURS_FORMAT_LABEL[f], f === (org.hoursFormat ?? 'DECIMAL')))
              .join('')),
            'Airframe hours, times since overhaul, block time and what is left before something falls due',
          )}
          ${field(
            'Printed alongside',
            select('documentSecondLanguage', [{ code: '', label: 'Nothing — one language only' }, ...DOC_LANGUAGES]
              .map((l) => opt(l.code, l.label, l.code === (org.documentSecondLanguage ?? '')))
              .join('')),
            'For whoever else has to read it — an owner, an insurer, a shop abroad',
          )}
        </div>
        <div class="frow">
          ${field(
            'Dates are written',
            select('dateFormat', DATE_FORMATS
              .map((f) => opt(f, DATE_FORMAT_LABEL[f], f === (org.dateFormat ?? 'D_MMM_YYYY')))
              .join('')),
            'On every screen and in every date box, in the digest and in the helper’s answers',
          )}
        </div>
        <div data-doclang-note></div>
        <div data-hours-note></div>
        <div data-dates-note></div>
      </div>
    `,
    onRender: (root) => {
      // The reference is built from three fields at once, so it is shown rather
      // than described: "AIR-2026-0250" answers the question the three separate
      // boxes do not.
      const preview = () => {
        const series = (val(root, 'invoiceSeries') || 'INV').toUpperCase();
        const number = String(numVal(root, 'invoiceNextNumber') ?? 1).padStart(4, '0');
        const withYear = root.querySelector<HTMLInputElement>('[data-year]')?.checked ?? true;
        const el = root.querySelector<HTMLElement>('[data-preview]');
        if (el) el.textContent = withYear ? `${series}-${new Date().getUTCFullYear()}-${number}` : `${series}-${number}`;
      };
      preview();
      root.querySelectorAll('[name="invoiceSeries"], [name="invoiceNextNumber"], [data-year]').forEach((el) =>
        el.addEventListener('input', preview),
      );
      root.querySelector('[data-year]')?.addEventListener('change', preview);

      /**
       * Says what the two boxes add up to, and says what is not built yet.
       *
       * The second half matters more than the first. Storing a setting that no
       * document reads, without saying so, is how somebody discovers on the day
       * of an audit that their release certificates were in English all along.
       */
      const note = root.querySelector<HTMLElement>('[data-doclang-note]');
      const describe = () => {
        if (!note) return;
        const first = DOC_LANGUAGES.find((l) => l.code === val(root, 'documentLanguage'));
        const second = DOC_LANGUAGES.find((l) => l.code === val(root, 'documentSecondLanguage'));
        const same = first && second && first.code === second.code;
        note.innerHTML = callout('caution', '!', html`
          ${same
            ? html`The same language twice is one language — the second box will be ignored.`
            : second
              ? html`Documents will be issued in <b>${esc(first?.label ?? '')}</b>, with
                  <b>${esc(second.label)}</b> printed beside it.`
              : html`Documents will be issued in <b>${esc(first?.label ?? '')}</b> only.`}
          This is stored, but <b>no document renders it yet</b> — the templates have still to be
          drawn up, and until they are every document prints in English as before.
        `);
      };
      describe();
      root.querySelectorAll('[name="documentLanguage"], [name="documentSecondLanguage"]').forEach((el) =>
        el.addEventListener('change', describe),
      );

      /**
       * Shows the same figure both ways, and says what the setting does not
       * reach.
       *
       * The second half is the part worth the space. Somebody who sets hours
       * and minutes and then finds a 2 000 h TBO still written in decimals
       * will read it as a place the setting was forgotten. It was not: a limit
       * is quoted from the manufacturer's manual, and "2000:00" misquotes the
       * source.
       */
      const hoursNote = root.querySelector<HTMLElement>('[data-hours-note]');
      const describeHours = () => {
        if (!hoursNote) return;
        const minutes = val(root, 'hoursFormat') === 'HOURS_MINUTES';
        const sample = 3411.9;
        hoursNote.innerHTML = callout('info', 'i', html`
          An aircraft at <b>${esc(minutes ? fmtHoursMinutes(sample) : '3,411.9')} h</b> will read that way
          everywhere a counter, a time since overhaul, a block time or a margin is shown — on the screens,
          in the nightly digest, on a certificate of release, and in the helper's answers.
          <br><br>
          <b>Limits, man-hours and rates stay decimal.</b> A 2 000 h TBO and a ±10 h tolerance are quoted
          from the manufacturer's manual, and labour at 6.5 h × a rate belongs on an invoice as a decimal.
          Nothing about how hours are stored changes either way.
        `);
      };
      describeHours();
      root.querySelector('[name="hoursFormat"]')?.addEventListener('change', describeHours);

      /**
       * Shows the day written the chosen way, and says the one place the
       * setting does not reach — PDFs, which stay ISO on purpose.
       */
      const datesNote = root.querySelector<HTMLElement>('[data-dates-note]');
      const describeDates = () => {
        if (!datesNote) return;
        const f = (val(root, 'dateFormat') || 'D_MMM_YYYY') as DateFormat;
        const sample = formatDate(new Date('2026-08-03T00:00:00.000Z'), f);
        datesNote.innerHTML = callout('info', 'i', html`
          The third of August will read <b>${esc(sample)}</b> on every screen, in every box a date is typed
          into, in the nightly digest and in the helper's answers. A date box takes it typed that way or from
          the calendar beside it.
          <br><br>
          <b>One thing it does not reach.</b> PDFs — a certificate of release, an estimate, an invoice — write
          dates as 2026-08-03, the ISO form, so they read the same in every country.
        `);
      };
      describeDates();
      root.querySelector('[name="dateFormat"]')?.addEventListener('change', describeDates);
    },
    collect: (root) => {
      const name = val(root, 'name');
      if (!name) return { error: 'The organisation needs a name.' };
      const series = val(root, 'invoiceSeries').toUpperCase();
      if (!series) return { error: 'An invoice series is required — it is the prefix every reference carries.' };
      const next = numVal(root, 'invoiceNextNumber');
      if (!next || next < 1) return { error: 'The next invoice number has to be 1 or more.' };
      const vat = numVal(root, 'vatRatePercent');
      if (vat !== undefined && (vat < 0 || vat > 100)) return { error: 'A VAT rate is a percentage between 0 and 100.' };

      // Empty means "not recorded", so blanks are sent as null rather than as
      // an empty string — otherwise clearing a field would store one.
      const text = (n: string) => val(root, n) || null;
      return {
        name,
        legalName: text('legalName'),
        addressLine1: text('addressLine1'),
        addressLine2: text('addressLine2'),
        postCode: text('postCode'),
        city: text('city'),
        countryCode: val(root, 'countryCode').toUpperCase() || null,
        phone: text('phone'),
        email: text('email'),
        website: text('website'),
        companyRegistration: text('companyRegistration'),
        vatNumber: text('vatNumber'),
        bankName: text('bankName'),
        bankAccount: text('bankAccount'),
        bankBic: text('bankBic'),
        vatRatePercent: vat ?? null,
        stockMarkupPercent: numVal(root, 'stockMarkupPercent') ?? null,
        labelReference: val(root, 'labelReference').trim() || null,
        invoiceSeries: series,
        invoiceNextNumber: next,
        invoiceIncludeYear: root.querySelector<HTMLInputElement>('[data-year]')?.checked ?? true,
        defaultCurrency: val(root, 'defaultCurrency') || undefined,
        documentLanguage: val(root, 'documentLanguage') || undefined,
        // Sent even when empty: an empty string is how "no second language" is
        // expressed, and `undefined` would leave the previous one in place.
        documentSecondLanguage: val(root, 'documentSecondLanguage'),
        hoursFormat: val(root, 'hoursFormat') || undefined,
        dateFormat: val(root, 'dateFormat') || undefined,
      };
    },
    submit: (payload) => api.organisation.update(payload),
    done: () => 'Organisation details saved',
    onDone: reload,
  });
}

/**
 * The rate card, edited as the table it is.
 *
 * The whole card is sent at once because that is what is on screen: saving a
 * price per keystroke would leave a card half-changed if the connection drops
 * mid-edit, and a half-changed price list is worse than an old one.
 *
 * A code already on the card cannot be renamed. Recorded chargeables carry the
 * code as text, so renaming would not corrupt anything already billed — it
 * would just quietly stop matching, which is the same silent failure this
 * screen exists to end. Remove it and add the new one instead.
 */
function editRates(o: OrgOverview, reload: () => void): void {
  interface Row {
    key: number;
    code: string;
    description: string;
    unit: string;
    unitPrice: string;
    appliesToRegime: string | null;
    /** Already on the card, so its code is fixed. */
    existing: boolean;
  }

  let nextKey = 1;
  const rows: Row[] = (o.rateCard?.items ?? []).map((i) => ({
    key: nextKey++,
    code: i.code,
    description: i.description,
    unit: i.unit,
    unitPrice: (i.unitPrice ?? 0).toFixed(2),
    appliesToRegime: i.appliesToRegime,
    existing: true,
  }));

  // An organisation with no card starts from the codes the application bills
  // from, priced at nothing — filling in three numbers beats inventing the
  // codes from scratch and getting one of them wrong.
  if (!rows.length) {
    for (const c of o.rateCardCodes.filter((c) => c.code !== 'ARC_EXTEND' && !c.code.startsWith('RECORDS_'))) {
      rows.push({
        key: nextKey++,
        code: c.code,
        description: c.description,
        unit: c.unit,
        unitPrice: '0.00',
        appliesToRegime: null,
        existing: false,
      });
    }
  }

  /** Pull what is on screen back into the rows before anything re-renders. */
  const harvest = (root: HTMLElement) => {
    for (const row of rows) {
      const at = (what: string) =>
        root.querySelector<HTMLInputElement>(`[name="${what}-${row.key}"]`)?.value;
      row.description = at('desc') ?? row.description;
      row.unit = at('unit') ?? row.unit;
      row.unitPrice = at('price') ?? row.unitPrice;
      if (!row.existing) row.code = (at('code') ?? row.code).toUpperCase();
    }
  };

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Rates',
    sub: o.rateCard ? o.rateCard.name : 'No rate card yet — this creates one',
    primary: 'Save rates',
    body: () => {
      const unused = o.rateCardCodes.filter((c) => !rows.some((r) => r.code === c.code));
      return html`
        <div class="frow">
          ${field('Card name', input('name', o.rateCard?.name ?? 'Standard rates', 'maxlength="160"'))}
          ${field(
            'Currency',
            input('currency', o.rateCard?.currency ?? o.organisation.defaultCurrency, 'maxlength="3" style="text-transform:uppercase;font-family:var(--mono)"'),
          )}
        </div>

        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr><th>Code</th><th class="wide">Description</th><th>Unit</th><th class="n">Price</th><th></th></tr>
            </thead>
            <tbody>
              ${rows
                .map(
                  (r) => html`<tr>
                    <td class="mono" style="white-space:nowrap">
                      ${r.existing
                        ? esc(r.code)
                        : input(`code-${r.key}`, r.code, 'maxlength="40" style="text-transform:uppercase;font-family:var(--mono);width:17ch"')}
                    </td>
                    <td class="wide">
                      ${input(`desc-${r.key}`, r.description, 'maxlength="300"')}
                      <span class="sub2">${esc(o.rateCardCodes.find((c) => c.code === r.code)?.drives ?? 'Invoiced by hand')}</span>
                    </td>
                    <td>
                      ${select(`unit-${r.key}`, o.rateUnits.map((u) => opt(u, u.toLowerCase(), u === r.unit)).join(''))}
                    </td>
                    <td class="n">
                      ${input(`price-${r.key}`, r.unitPrice, 'type="number" min="0" step="0.01" style="text-align:right;width:11ch;font-family:var(--mono)"')}
                    </td>
                    <td><button type="button" class="btn sm" data-drop="${r.key}">Remove</button></td>
                  </tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </div>

        <div style="display:flex;gap:8px;align-items:flex-end;margin-top:10px;flex-wrap:wrap">
          ${unused.length
            ? field(
                'Add a standard line',
                select('addCode', [opt('', 'choose one…'), ...unused.map((c) => opt(c.code, `${c.code} — ${c.description}`))].join(''), 'data-add-code'),
              )
            : ''}
          <button type="button" class="btn sm" data-add-custom>Add a line of your own</button>
        </div>

        ${callout('info', 'i', html`
          <b>ENGINEERING_HOUR</b> prices the labour on a work pack and is charged when the release is
          signed; <b>ARC_ISSUE</b> is charged when a review certificate is issued. The rest are billed by
          hand. A line priced at 0.00 is treated as no price at all — nothing gets raised for it.
        `)}
      `;
    },

    onRender: (root, refresh) => {
      root.querySelectorAll<HTMLElement>('[data-drop]').forEach((b) =>
        b.addEventListener('click', () => {
          harvest(root);
          const key = Number(b.dataset.drop);
          rows.splice(rows.findIndex((r) => r.key === key), 1);
          refresh();
        }),
      );

      root.querySelector<HTMLSelectElement>('[data-add-code]')?.addEventListener('change', (e) => {
        const code = (e.target as HTMLSelectElement).value;
        if (!code) return;
        harvest(root);
        const standard = o.rateCardCodes.find((c) => c.code === code)!;
        rows.push({
          key: nextKey++,
          code: standard.code,
          description: standard.description,
          unit: standard.unit,
          unitPrice: '0.00',
          appliesToRegime: null,
          existing: false,
        });
        refresh();
      });

      root.querySelector('[data-add-custom]')?.addEventListener('click', () => {
        harvest(root);
        rows.push({
          key: nextKey++,
          code: '',
          description: '',
          unit: 'HOUR',
          unitPrice: '0.00',
          appliesToRegime: null,
          existing: false,
        });
        refresh();
      });
    },

    collect: (root) => {
      harvest(root);
      if (!rows.length) {
        return { error: 'A rate card needs at least one line. With none, nothing signed off would ever be billed.' };
      }
      const seen = new Set<string>();
      for (const r of rows) {
        if (!/^[A-Z0-9_]{2,40}$/.test(r.code)) {
          return { error: `"${r.code || '(blank)'}" is not a usable code — letters, digits and underscores only.` };
        }
        if (seen.has(r.code)) return { error: `${r.code} appears twice. Each code may appear once on a card.` };
        seen.add(r.code);
        if (!r.description.trim()) return { error: `${r.code} needs a description — it is what appears on the invoice line.` };
        if (!Number.isFinite(Number(r.unitPrice)) || Number(r.unitPrice) < 0) {
          return { error: `${r.code} needs a price of 0 or more.` };
        }
      }
      return {
        name: val(root, 'name') || undefined,
        currency: val(root, 'currency').toUpperCase() || undefined,
        items: rows.map((r) => ({
          code: r.code,
          description: r.description.trim(),
          unit: r.unit,
          unitPrice: Number(r.unitPrice),
          appliesToRegime: r.appliesToRegime,
        })),
      };
    },
    submit: (payload) => api.organisation.updateRateCard(payload),
    done: (r) =>
      r.created
        ? `Rate card created — ${r.lines} line(s)`
        : `Rates saved — ${r.lines} line(s)${r.removed ? `, ${r.removed} removed` : ''}`,
    onDone: reload,
  });
}

const PERSON_FIELD_LABEL: Record<string, string> = {
  firstName: 'first name',
  lastName: 'last name',
  email: 'email',
  phone: 'phone',
  jobTitle: 'role',
  licenceKind: 'licence kind',
  licenceNumber: 'licence number',
  licenceCategory: 'licence category',
  licenceExpiry: 'licence expiry',
  isPilotOwner: 'pilot-owner',
};

/**
 * Correcting a person's record.
 *
 * Split like the aircraft and component records, for the reason that matters
 * here: an authorisation is granted against a licence, and a signature already
 * made recorded what was held at the time. So contact details change freely
 * while a name or a licence is a correction, with the before, the after and the
 * reason on the audit trail.
 *
 * This is also where a licence first gets recorded for somebody who was created
 * without one — which is most people, since the account that creates an
 * organisation gets a Person automatically and no form ever asked.
 */
function editPerson(person: OrgPerson, reload: () => void): void {
  let correcting = false;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Edit ${person.displayName}`,
    sub: person.licence ?? 'No licence on file — a release authorisation needs one',
    primary: 'Save',

    body: () => html`
      <div class="fset">
        <div class="lg">Contact</div>
        ${field('Role', input('jobTitle', person.jobTitle ?? '', 'placeholder="Certifying staff" maxlength="120"'))}
        <div class="frow">
          ${field('Email', input('email', person.email ?? '', 'type="email" maxlength="254"'))}
          ${field('Phone', input('phone', person.phone ?? '', 'maxlength="40"'))}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Correct the record</div>
        <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
          <input type="checkbox" data-correcting ${correcting ? 'checked' : ''} style="margin-top:3px">
          <span>I need to change the <b>name or the licence</b>.</span>
        </label>

        ${correcting
          ? html`
              ${callout('caution', '!', html`
                Authorisations are granted against the licence, and every signature already made recorded
                what was held at the time. Correcting it here does not change those — it changes what holds
                from now on, and the change is recorded with what it was, what it became and why.
              `)}

              <div class="frow">
                ${field('First name', input('firstName', person.firstName, 'maxlength="80"'))}
                ${field('Last name', input('lastName', person.lastName, 'maxlength="80"'))}
              </div>

              <div class="frow3">
                ${field('Licence kind', select('licenceKind', [
                  opt('NONE', 'None', !person.licenceKind),
                  opt('PART_66', 'Part-66', person.licenceKind === 'PART_66'),
                  opt('FAA_AP', 'FAA A&P', person.licenceKind === 'FAA_AP'),
                  opt('FAA_IA', 'FAA A&P with IA', person.licenceKind === 'FAA_IA'),
                ].join('')))}
                ${field('Number', input('licenceNumber', person.licenceNumber ?? '', 'placeholder="RO.66.2210" style="font-family:var(--mono)" maxlength="60"'))}
                ${field('Category', input('licenceCategory', person.licenceCategory ?? '', 'placeholder="B1.2" maxlength="24"'))}
              </div>
              ${field('Expiry', input('licenceExpiry', person.licenceExpiry?.slice(0, 10) ?? '', 'type="date"'),
                'Required with a licence — without it nothing can tell you it has run out')}

              ${field('Reason for the correction',
                input('reason', '', 'placeholder="Part-66 licence issued 14/02/2026; recorded from the certificate" maxlength="400"'),
                'Recorded with the before and after')}
            `
          : ''}
      </div>

      ${callout('info', 'i', html`
        Authorisations already granted are not edited here. They record what this person was permitted to
        do on a given date, so a wrong one is revoked and granted again — which leaves both on the file.
      `)}
    `,

    onRender: (root, refresh) => {
      root.querySelector<HTMLInputElement>('[data-correcting]')?.addEventListener('change', (e) => {
        correcting = (e.target as HTMLInputElement).checked;
        refresh();
      });
    },

    collect: (root) => {
      const payload: Record<string, unknown> = {
        jobTitle: val(root, 'jobTitle').trim() || undefined,
        email: val(root, 'email').trim() || undefined,
        phone: val(root, 'phone').trim() || undefined,
      };

      if (correcting) {
        const firstName = val(root, 'firstName').trim();
        const lastName = val(root, 'lastName').trim();
        if (!firstName || !lastName) return { error: 'Both names are required.' };
        const kind = val(root, 'licenceKind');
        Object.assign(payload, {
          firstName,
          lastName,
          licenceKind: kind,
          licenceNumber: val(root, 'licenceNumber').trim() || undefined,
          licenceCategory: val(root, 'licenceCategory').trim() || undefined,
          licenceExpiry: val(root, 'licenceExpiry') || undefined,
          reason: val(root, 'reason').trim() || undefined,
        });
      }
      return payload;
    },

    submit: (payload) => api.organisation.updatePerson(person.id, payload),
    done: (r) =>
      r.unchanged
        ? 'Nothing changed'
        : `${r.displayName} updated — ${r.changed.map((f: string) => PERSON_FIELD_LABEL[f] ?? f).join(', ')}${
            r.licenceOnFile && r.changed.some((f: string) => f.startsWith('licence'))
              ? ' · a release authorisation can be granted now'
              : ''
          }`,
    onDone: reload,
  });
}

function grantAuthorisation(o: OrgOverview, person: OrgPerson, reload: () => void): void {
  const held = new Set(person.authorisations.map((a) => a.kind));
  const available = o.authorisationKinds.filter((k) => !held.has(k.kind));
  let kind = available[0]?.kind ?? '';

  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Authorise ${person.displayName}`,
    sub: person.licence ?? 'No licence on file',
    primary: 'Grant authorisation',
    body: () => {
      const chosen = o.authorisationKinds.find((k) => k.kind === kind);
      return html`
        ${
          available.length
            ? html`
                ${field('Authorisation', select('kind', available.map((k) => opt(k.kind, k.label, k.kind === kind)).join(''), 'data-kind'))}
                ${chosen ? callout('info', 'i', esc(chosen.note)) : ''}
                ${field('Scope', input('scopeNote', '', 'placeholder="Part-ML fleet — issue Form 15c" maxlength="400"'))}
                <div class="frow3">
                  ${field('Reference', input('reference', '', 'placeholder="ARS-001" style="font-family:var(--mono)" maxlength="60"'))}
                  ${field('Granted', input('grantedAt', todayISO(), 'type="date"'))}
                  ${field('Expires', input('expiresAt', '', 'type="date"'))}
                </div>
                ${
                  kind.startsWith('CRS') && !person.licence
                    ? callout('exceed', '✕', html`
                        ${esc(person.displayName)} has no licence recorded. A release authorisation cannot be
                        granted until one is on file.
                      `)
                    : ''
                }
              `
            : callout('ok', '✓', html`
                ${esc(person.displayName)} already holds every authorisation this application recognises.
              `)
        }
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
        kind = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const k = val(root, 'kind');
      if (!k) return { error: 'Nothing left to grant.' };
      return {
        kind: k,
        scopeNote: val(root, 'scopeNote') || undefined,
        reference: val(root, 'reference') || undefined,
        grantedAt: val(root, 'grantedAt') || undefined,
        expiresAt: val(root, 'expiresAt') || undefined,
      };
    },
    submit: (payload) => api.organisation.grant(person.id, payload),
    done: (r) => `${person.displayName} may now ${(AUTH_SHORT[r.kind] ?? r.kind).toLowerCase()}`,
    onDone: reload,
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

/** Organisation-level records only — the aircraft ones live on the aircraft. */
const orgDocs = (reg: DocumentRegister) => reg.documents.filter((d) => !d.aircraftId);

/** The notification card's own handlers — save, and show what it would say. */
function bindNotifications(host: HTMLElement, reload: () => void): void {
  const read = (): Record<string, unknown> => {
    const box = (name: string) =>
      host.querySelector<HTMLInputElement>(`[data-notif="${name}"]`)?.checked ?? false;
    return {
      frequency: host.querySelector<HTMLSelectElement>('[data-notif-frequency]')?.value ?? 'WEEKLY',
      weekday: Number(host.querySelector<HTMLSelectElement>('[name="weekday"]')?.value ?? 1),
      horizonDays: Number(host.querySelector<HTMLInputElement>('[name="horizonDays"]')?.value ?? 30),
      includeOverdue: box('includeOverdue'),
      includeDueSoon: box('includeDueSoon'),
      includeArcExpiry: box('includeArcExpiry'),
      includeDefects: box('includeDefects'),
      includeDeferrals: box('includeDeferrals'),
      includeShelfLife: box('includeShelfLife'),
    };
  };

  host.querySelector('[data-notif-save]')?.addEventListener('click', async () => {
    const body = read();
    if (!(Number(body.horizonDays) >= 1)) return toast('How far ahead? A day at least.', 'error');
    try {
      await api.notifications.save(body);
      toast(body.frequency === 'OFF' ? 'Nothing will be sent.' : 'Saved.');
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That could not be saved.', 'error');
    }
  });

  /**
   * Sends one message, and says what the mail server answered.
   *
   * The queue id is in the toast on purpose. "It did not arrive" is answered
   * at the mail provider, not here, and without the id nobody can look it up.
   */
  const testButton = host.querySelector<HTMLButtonElement>('[data-notif-test]');
  testButton?.addEventListener('click', async () => {
    const was = testButton.textContent;
    testButton.disabled = true;
    testButton.textContent = 'Sending…';
    try {
      const r = await api.notifications.sendTest();
      const queued = /queued as (\S+)/.exec(r.response)?.[1];
      toast(`Sent to ${r.to}${queued ? ` — reference ${queued}` : ''}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That could not be sent.', 'error');
    } finally {
      testButton.disabled = false;
      testButton.textContent = was;
    }
  });

  host.querySelector('[data-notif-preview]')?.addEventListener('click', async () => {
    try {
      const digest = await api.notifications.preview();
      openDialog<null>({
        wide: true,
        title: 'What the next digest would say',
        sub: `${digest.total} item(s) across ${digest.horizonDays} days · nothing is sent by opening this`,
        primary: '',
        body: () =>
          digest.total
            ? digest.sections
                .map(
                  (section) => html`
                    <div class="card" style="margin-bottom:10px">
                      <div class="card-h"><div><h2>${esc(section.title)}</h2></div></div>
                      <div class="rows">
                        ${section.lines
                          .map(
                            (l) => html`
                              <div class="row">
                                <div class="bd">
                                  <div class="t"><span class="reg">${esc(l.registration)}</span> ${esc(l.what)}</div>
                                  <div class="m tiny ${l.tone === 'overdue' ? '' : 'dim'}"
                                       style="${l.tone === 'overdue' ? 'color:var(--exceed)' : ''}">${esc(l.when)}</div>
                                </div>
                              </div>
                            `,
                          )
                          .join('')}
                      </div>
                    </div>
                  `,
                )
                .join('')
            : callout('ok', '✓', html`
                <b>Nothing to report.</b> A digest with nothing in it is not sent — mail that says
                "all clear" every morning is mail people learn to filter.
              `),
        collect: () => null,
        submit: async () => null,
        done: () => '',
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That could not be read.', 'error');
    }
  });
}

export async function renderOrganisation(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading organisation');
  try {
    const [o, reg, notifications, members, destinations] = await Promise.all([
      api.organisation.overview(),
      api.documents.list(),
      api.notifications.mine(),
      // Administrators only, so a 403 here is the ordinary answer for everybody
      // else rather than something to shout about. The card is simply absent.
      api.organisation.members().catch(() => null),
      // Deployment configuration rather than organisation data: whether this
      // installation has a Google client at all. Absent is a normal answer.
      api.organisation.backupDestinations().catch(() => null),
    ]);
    const drive = destinations?.googleDrive ?? null;
    host.innerHTML = view(o, reg, notifications, members, drive);
    bindNotifications(host, () => void renderOrganisation(host));

    const reload = () => void renderOrganisation(host);
    if (members) bindMembers(host, members, o, reload);
    // Same test as the members card: only an administrator gets that call back,
    // and a backup is every record the organisation holds in one file.
    if (members) bindRecords(host, o, reload, drive);

    const open = (what: string) => {
      if (what === 'edit-organisation') return editOrganisation(o, reload);
      if (what === 'edit-rates') return editRates(o, reload);
      if (what === 'add-customer') return addCustomer(reload);
      if (what === 'add-approval') return addApproval(reload);
      // One dialog for both, because 'a staff record' and 'a login' is a
      // distinction nobody should have to make before they have started.
      if (what === 'add-person') return addSomebody(o, members, reload);
      if (what === 'add-authorisation') {
        const person = o.people[0];
        if (!person) {
          toast('Add somebody first — an authorisation is granted to a person.', 'error');
          return addSomebody(o, members, reload);
        }
        return grantAuthorisation(o, person, reload);
      }
    };

    host.querySelectorAll<HTMLElement>('[data-edit-customer]').forEach((b) =>
      b.addEventListener('click', () => {
        const customer = o.customers.find((c) => c.id === b.dataset.editCustomer);
        if (customer) addCustomer(reload, customer);
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        // Edit sits inside a <summary>, where a click would otherwise fold the
        // panel shut underneath the dialog that just opened.
        e.preventDefault();
        open(b.dataset.open!);
      }),
    );

    // Provider-level, so it is not on the edit form with the invoice address:
    // it is not the organisation's own setting to change.
    host.querySelector<HTMLInputElement>('[data-feature-stores]')?.addEventListener('change', async (e) => {
      const box = e.target as HTMLInputElement;
      const wanted = box.checked;
      try {
        await api.organisation.setFeatures({ storesEnabled: wanted });
        toast(wanted ? 'Stores turned on.' : 'Stores turned off.');
        // A full reload: the navigation is built from the session, so the menu
        // item has to come and go with it.
        location.assign('/people');
      } catch (err) {
        box.checked = !wanted;
        toast(err instanceof Error ? err.message : 'Could not change that.', 'error');
      }
    });

    bindDownloads(host);
    bindDocumentViewers(host);
    host.querySelector('[data-doc="upload"]')?.addEventListener('click', () => uploadDocument(reg, {}, reload));
    host.querySelector('[data-doc="verify"]')?.addEventListener('click', () => void verifyIntegrity());
    host.querySelectorAll<HTMLElement>('[data-remove]').forEach((b) =>
      b.addEventListener('click', () => {
        const doc = reg.documents.find((d) => d.id === b.dataset.remove);
        if (doc) removeDocument(doc, reload);
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-revise]').forEach((b) =>
      b.addEventListener('click', () => {
        const doc = reg.documents.find((d) => d.id === b.dataset.revise);
        if (doc) reviseDocument(doc, reload);
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-grant]').forEach((b) =>
      b.addEventListener('click', () => {
        const person = o.people.find((p) => p.id === b.dataset.grant);
        if (person) grantAuthorisation(o, person, reload);
      }),
    );

    host.querySelectorAll<HTMLElement>('[data-leaving]').forEach((b) =>
      b.addEventListener('click', () => {
        const person = o.people.find((p) => p.id === b.dataset.leaving);
        if (person) recordLeaving(person, reload);
      }),
    );

    const former = host.querySelector<HTMLElement>('[data-former]');
    host.querySelector<HTMLInputElement>('[data-show-former]')?.addEventListener('change', (e) => {
      if (former) former.hidden = !(e.target as HTMLInputElement).checked;
    });

    host.querySelectorAll<HTMLElement>('[data-edit-person]').forEach((b) =>
      b.addEventListener('click', () => {
        const person = o.people.find((p) => p.id === b.dataset.editPerson);
        if (person) editPerson(person, reload);
      }),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load the organisation.');
  }
}
